import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Play, Check, Loader2, ArrowLeft, FileText, DownloadCloud, BookOpen, Award } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import { useAuth } from "@/context/AuthContext";
import VideoEmbed from "@/components/VideoEmbed";
import Seo from "@/components/Seo";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [provider, setProvider] = useState("pesapal");
  const [buying, setBuying] = useState(false);

  const load = () =>
    api.get(`/courses/${slug}`).then((r) => {
      setData(r.data);
      let idx = r.data.lessons.findIndex((l) => l.id === r.data.resume_lesson_id);
      if (idx < 0) idx = r.data.lessons.findIndex((l) => !l.locked);
      setActive(idx >= 0 ? idx : 0);
    }).catch(() => setData(null));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, user]);

  const markComplete = async () => {
    const cur = data.lessons[active];
    if (!cur) return;
    try {
      const { data: res } = await api.post(`/courses/${data.product.id}/lessons/${cur.id}/complete`, { completed: !cur.completed });
      if (res.certificate) toast.success("Course complete — your certificate is ready!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDoc = async (doc, download) => {
    try {
      const res = await api.get(`/courses/documents/${doc.id}/${download ? "download" : "view"}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = doc.file_name; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open document"); }
  };

  const buy = async () => {
    if (!user) { toast.info("Sign in to enroll"); navigate("/login"); return; }
    setBuying(true);
    try {
      const { data: order } = await api.post("/orders", { items: [{ product_id: data.product.id, quantity: 1 }], provider });
      if (order.checkout_url && /^https?:\/\//.test(order.checkout_url)) window.location.href = order.checkout_url;
      else navigate(`/checkout/pay/${order.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start checkout"); }
    finally { setBuying(false); }
  };

  if (!data) return <div className="p-24 text-center text-[color:var(--ink-muted)]">Loading course…</div>;
  const { product, lessons, owns } = data;
  const current = lessons[active];
  const pct = data.lesson_count ? Math.round((data.completed_count / data.lesson_count) * 100) : 0;
  const resume = () => {
    let idx = lessons.findIndex((l) => l.id === data.resume_lesson_id);
    if (idx < 0) idx = lessons.findIndex((l) => !l.locked);
    if (idx >= 0) { setActive(idx); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
      <Seo title={product.title} description={product.subtitle || (product.description || "").slice(0, 155)} image={product.image_url} type="video.other" />
      <Link to="/store/courses" className="inline-flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)] hover:text-[color:var(--evergreen)]">
        <ArrowLeft size={12} /> All courses
      </Link>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Course</p>
          <h1 data-testid="course-title" className="font-editorial text-4xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-5xl">{product.title}</h1>
          <p className="mt-3 max-w-2xl text-base text-[color:var(--ink-body)]">{product.subtitle || product.description}</p>
        </div>
        {owns ? (
          <div className="w-full max-w-xs" data-testid="course-enrolled">
            <div className="flex items-center justify-between">
              <span className="chip chip-emerald">Enrolled</span>
              <span className="text-xs font-semibold text-[color:var(--evergreen)]" data-testid="course-progress-pct">{pct}%</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[color:var(--paper-ink)]" data-testid="course-progress-bar">
              <div className="h-full rounded-full bg-[color:var(--evergreen)] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-sm text-[color:var(--ink-muted)]" data-testid="course-progress">{data.completed_count}/{data.lesson_count} lessons complete</p>
            {pct < 100 && (
              <button onClick={resume} data-testid="course-resume" className="btn-edge mt-3 w-full justify-center text-xs">
                <Play size={13} /> Resume where you left off
              </button>
            )}
            {pct === 100 && data.certificate && (
              <Link to={`/certificate/${data.certificate.code}`} data-testid="course-certificate" className="btn-edge mt-3 w-full justify-center text-xs">
                <Award size={13} /> View your certificate
              </Link>
            )}
          </div>
        ) : data.admin_preview ? (
          <div className="w-full max-w-xs" data-testid="course-admin-preview">
            <span className="chip chip-emerald">Admin preview</span>
            <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Content unlocked for review. Purchases and progress aren’t tracked for admin accounts.</p>
          </div>
        ) : (
          <div className="text-right">
            <p className="font-editorial text-3xl text-[color:var(--evergreen)]">${product.price.toFixed(2)}</p>
            <span className="text-xs text-[color:var(--ink-muted)]">{product.currency}</span>
          </div>
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        {/* Player */}
        <div>
          {current && !current.locked && current.video_url ? (
            <VideoEmbed url={current.video_url} />
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-[color:var(--ink-hairline)] bg-[color:var(--paper-ink)] text-center">
              <Lock size={22} className="text-[color:var(--ink-muted)]" />
              <p className="max-w-xs text-sm text-[color:var(--ink-body)]">
                {(owns || data.admin_preview) ? "Select a lesson to start watching." : "Enroll to unlock every lesson in this course."}
              </p>
            </div>
          )}
          {current && (
            <div className="mt-5">
              <h2 className="font-editorial text-2xl font-semibold text-[color:var(--ink)]">{current.title}</h2>
              {current.description && <p className="mt-2 text-sm text-[color:var(--ink-body)]">{current.description}</p>}
              {owns && !current.locked && (
                <button onClick={markComplete} data-testid="lesson-complete-toggle"
                  className={`mt-4 inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm ${current.completed ? "border-[color:var(--evergreen)] bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]" : "border-[color:var(--ink-hairline)] text-[color:var(--ink-body)] hover:border-[color:var(--evergreen)]"}`}>
                  <Check size={14} /> {current.completed ? "Completed — undo" : "Mark lesson complete"}
                </button>
              )}
            </div>
          )}
          {!owns && (
            <div className="card-moss mt-6 p-6" data-testid="course-enroll-card">
              <p className="overline mb-3">Enroll</p>
              {data.admin_preview && (
                <p className="mb-4 text-xs text-[color:var(--ink-muted)]">You’re viewing this as an admin. Content is unlocked for review — use the checkout below to test the purchase flow.</p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {[{ id: "pesapal", label: "PesaPal", note: "Cards & mobile money" }, { id: "nowpayments", label: "NowPayments", note: "Crypto" }].map((pv) => (
                  <button key={pv.id} onClick={() => setProvider(pv.id)} data-testid={`course-provider-${pv.id}`}
                    className={`rounded-md border p-3 text-left ${provider === pv.id ? "border-[color:var(--evergreen)] bg-[color:var(--evergreen-soft)]" : "border-[color:var(--ink-hairline)]"}`}>
                    <span className="font-display text-sm font-semibold text-[color:var(--ink)]">{pv.label}</span>
                    <p className="text-[11px] text-[color:var(--ink-muted)]">{pv.note}</p>
                  </button>
                ))}
              </div>
              <button onClick={buy} disabled={buying} data-testid="course-buy" className="btn-edge mt-5 w-full justify-center">
                {buying ? <Loader2 size={16} className="animate-spin" /> : `Enroll for $${product.price.toFixed(2)}`}
              </button>
            </div>
          )}
        </div>

        {/* Lesson list */}
        <div>
          <p className="overline mb-3">{lessons.length} lessons</p>
          <div className="grid gap-2">
            {lessons.map((l, i) => {
              const openable = !l.locked;
              return (
                <button key={l.id} onClick={() => openable && setActive(i)} data-testid={`course-lesson-${i}`}
                  className={`flex items-center justify-between gap-3 rounded-md border p-4 text-left transition-colors ${
                    i === active ? "border-[color:var(--evergreen)] bg-[color:var(--evergreen-soft)]" : "border-[color:var(--ink-hairline)] bg-white hover:border-[color:var(--ink)]"
                  } ${openable ? "" : "opacity-70"}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-[color:var(--paper-ink)] text-[color:var(--evergreen)]">
                      {l.locked ? <Lock size={14} /> : <Play size={14} />}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-[color:var(--ink)]">{l.title}</p>
                      <p className="text-[11px] text-[color:var(--ink-muted)]">
                        {l.completed ? "Completed" : l.is_preview ? "Free preview" : l.locked ? "Locked" : "Available"}{l.duration ? ` · ${l.duration}` : ""}
                      </p>
                    </div>
                  </div>
                  {l.completed ? <Check size={16} className="text-[color:var(--evergreen)]" /> : (!l.locked && i === active && <span className="h-2 w-2 rounded-full bg-[color:var(--evergreen)]" />)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {owns && data.documents?.length > 0 && (
        <section className="mt-12" data-testid="course-materials">
          <div className="mb-4 flex items-center gap-2"><BookOpen size={16} className="text-[color:var(--evergreen)]" /><p className="overline">Course materials</p></div>
          <div className="grid gap-2 md:grid-cols-2">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--ink-hairline)] bg-white p-4" data-testid={`course-doc-${d.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-[color:var(--paper-ink)] text-[color:var(--evergreen)]"><FileText size={16} /></span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[color:var(--ink)]">{d.title}</p>
                    <p className="text-[11px] text-[color:var(--ink-muted)]">{d.ext?.toUpperCase()} · {(d.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  {d.viewable && (
                    <button onClick={() => openDoc(d, false)} data-testid={`course-doc-view-${d.id}`} className="rounded-md border border-[color:var(--ink-hairline)] px-3 py-1.5 text-xs text-[color:var(--ink-body)] hover:border-[color:var(--evergreen)] hover:text-[color:var(--evergreen)]">Read</button>
                  )}
                  <button onClick={() => openDoc(d, true)} data-testid={`course-doc-download-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-[color:var(--ink-hairline)] px-3 py-1.5 text-xs text-[color:var(--ink-body)] hover:border-[color:var(--evergreen)] hover:text-[color:var(--evergreen)]"><DownloadCloud size={13} /> Download</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
